// Constants and mappings for the face-photo (facePhoto/Photo) validation feature.
// The backend runs image rules on the uploaded photo and, when the photo is
// rejected, returns HTTP 422 with a `validationResult` payload. These constants
// are intentionally data-driven so new rules can be supported by adding a single
// entry to the maps below.

// The upload endpoint returns this status when the photo violates image rules
// in enforce mode (upload is rejected).
export const PHOTO_VALIDATION_HTTP_STATUS = 422;

// Backend validation modes. WARN_ONLY still stores the photo and returns HTTP
// 200 with a `validationResult` + warnings; ENFORCE rejects with HTTP 422.
export enum PHOTO_VALIDATION_MODE {
  WARN_ONLY = 'WARN_ONLY',
  ENFORCE = 'ENFORCE',
}

export enum PHOTO_RULE_STATUS {
  PASSED = 'PASSED',
  FAILED = 'FAILED',
  SKIPPED = 'SKIPPED',
}

export interface IPhotoValidationRule {
  ruleName: string;
  status: string;
  message?: string | null;
  confidence?: number | null;
}

export interface IPhotoValidationResult {
  passed: boolean;
  validationMode?: string;
  summary?: string;
  allRules: IPhotoValidationRule[];
  failedRules: IPhotoValidationRule[];
}

// Shape of the HTTP 422 body returned by the upload endpoint.
export interface IPhotoUploadErrorResponse {
  location?: string | null;
  error?: string;
  validationResult?: IPhotoValidationResult;
}

interface ITranslatableText {
  // i18n key (added to the locale files) ...
  key: string;
  // ... with an English fallback so any locale missing the key still renders
  // readable text instead of the raw key.
  default: string;
}

// Backend rule enum -> friendly, human readable label.
// Add new rules here to support them everywhere in the UI.
export const PHOTO_RULE_DISPLAY_NAMES: Record<string, ITranslatableText> = {
  UPLOAD_RESTRICTIONS: {
    key: 'PHOTO_RULE_UPLOAD_RESTRICTIONS',
    default: 'File requirements',
  },
  FACE_REQUIRED: { key: 'PHOTO_RULE_FACE_REQUIRED', default: 'Face detected' },
  SINGLE_FACE: { key: 'PHOTO_RULE_SINGLE_FACE', default: 'Single face' },
  FACE_FULLY_VISIBLE: {
    key: 'PHOTO_RULE_FACE_FULLY_VISIBLE',
    default: 'Face fully visible',
  },
  EYES_OPEN: { key: 'PHOTO_RULE_EYES_OPEN', default: 'Eyes open' },
  FACE_COVERAGE: { key: 'PHOTO_RULE_FACE_COVERAGE', default: 'Face size' },
  FACE_TILT: { key: 'PHOTO_RULE_FACE_TILT', default: 'Face angle' },
  DISALLOW_SUNGLASSES: {
    key: 'PHOTO_RULE_DISALLOW_SUNGLASSES',
    default: 'Sunglasses',
  },
  DISALLOW_OCCLUSION: {
    key: 'PHOTO_RULE_DISALLOW_OCCLUSION',
    default: 'No obstructions',
  },
  MIN_RESOLUTION: { key: 'PHOTO_RULE_MIN_RESOLUTION', default: 'Resolution' },
  BLUR: { key: 'PHOTO_RULE_BLUR', default: 'Sharpness' },
  BRIGHTNESS: { key: 'PHOTO_RULE_BRIGHTNESS', default: 'Brightness' },
  CONTRAST: { key: 'PHOTO_RULE_CONTRAST', default: 'Contrast' },
  BACKGROUND_COLOR: {
    key: 'PHOTO_RULE_BACKGROUND_COLOR',
    default: 'Background',
  },
};

// Backend rule enum -> frontend "how to fix" guidance (independent of the raw
// backend message, which is technical).
export const PHOTO_RULE_HOW_TO_FIX: Record<string, ITranslatableText> = {
  UPLOAD_RESTRICTIONS: {
    key: 'PHOTO_FIX_UPLOAD_RESTRICTIONS',
    default: 'Use a JPG or PNG image that meets the allowed file size and type.',
  },
  FACE_REQUIRED: {
    key: 'PHOTO_FIX_FACE_REQUIRED',
    default: 'Make sure your face is clearly visible and centred in the photo.',
  },
  SINGLE_FACE: {
    key: 'PHOTO_FIX_SINGLE_FACE',
    default: 'Only one person should be visible in the photo.',
  },
  FACE_FULLY_VISIBLE: {
    key: 'PHOTO_FIX_FACE_FULLY_VISIBLE',
    default: 'Keep your whole face visible and do not cover any part of it.',
  },
  EYES_OPEN: {
    key: 'PHOTO_FIX_EYES_OPEN',
    default: 'Keep both eyes open and look directly at the camera.',
  },
  FACE_COVERAGE: {
    key: 'PHOTO_FIX_FACE_COVERAGE',
    default:
      'Move closer to the camera so your face occupies more of the frame.',
  },
  FACE_TILT: {
    key: 'PHOTO_FIX_FACE_TILT',
    default: 'Keep your head straight and look directly at the camera.',
  },
  DISALLOW_SUNGLASSES: {
    key: 'PHOTO_FIX_DISALLOW_SUNGLASSES',
    default: 'Remove sunglasses before taking the photo.',
  },
  DISALLOW_OCCLUSION: {
    key: 'PHOTO_FIX_DISALLOW_OCCLUSION',
    default: 'Remove hats, masks or anything covering your face.',
  },
  MIN_RESOLUTION: {
    key: 'PHOTO_FIX_MIN_RESOLUTION',
    default: 'Use a higher resolution photo.',
  },
  BLUR: {
    key: 'PHOTO_FIX_BLUR',
    default: 'Hold the camera steady and improve lighting before taking the photo.',
  },
  BRIGHTNESS: {
    key: 'PHOTO_FIX_BRIGHTNESS',
    default:
      'Move to an evenly lit area and avoid very dark or very bright surroundings.',
  },
  CONTRAST: {
    key: 'PHOTO_FIX_CONTRAST',
    default: 'Take the photo in even, balanced lighting to improve contrast.',
  },
  BACKGROUND_COLOR: {
    key: 'PHOTO_FIX_BACKGROUND_COLOR',
    default:
      'Use a plain background in the colour shown. Avoid coloured or patterned backgrounds.',
  },
};

// The BACKGROUND_COLOR rule carries the required background colour as a hex
// value inside its message. The UI renders it as a colour swatch instead of
// exposing the raw hex to the user.
export const BACKGROUND_COLOR_RULE = 'BACKGROUND_COLOR';

export interface INamedColor {
  key: string;
  default: string;
  hex: string;
}

// Curated palette used to derive a friendly, translatable colour name from a
// hex value. The nearest colour within COLOR_NAME_MATCH_THRESHOLD (simple RGB
// distance) wins; if nothing is close enough, only the swatch is shown.
export const NAMED_COLORS: INamedColor[] = [
  { key: 'COLOR_NAME_WHITE', default: 'White', hex: '#FFFFFF' },
  { key: 'COLOR_NAME_OFF_WHITE', default: 'Off white', hex: '#F2F2F0' },
  { key: 'COLOR_NAME_CREAM', default: 'Cream', hex: '#F5F1E6' },
  { key: 'COLOR_NAME_BEIGE', default: 'Beige', hex: '#E8DCC4' },
  { key: 'COLOR_NAME_LIGHT_GREY', default: 'Light grey', hex: '#D3D3D3' },
  { key: 'COLOR_NAME_GREY', default: 'Grey', hex: '#808080' },
  { key: 'COLOR_NAME_DARK_GREY', default: 'Dark grey', hex: '#404040' },
  { key: 'COLOR_NAME_BLACK', default: 'Black', hex: '#000000' },
  { key: 'COLOR_NAME_LIGHT_BLUE', default: 'Light blue', hex: '#ADD8E6' },
  { key: 'COLOR_NAME_BLUE', default: 'Blue', hex: '#2F6FD1' },
  { key: 'COLOR_NAME_NAVY', default: 'Navy blue', hex: '#1B2A4A' },
  { key: 'COLOR_NAME_GREEN', default: 'Green', hex: '#3FA45B' },
  { key: 'COLOR_NAME_RED', default: 'Red', hex: '#D32F2F' },
];

export const COLOR_NAME_MATCH_THRESHOLD = 50;
